import type { FrameGraphCaptureRequest } from './capture.ts';
import type { InspectorTheme } from './theme.ts';
import { InspectorThemeController, resolveGraphTheme } from './panelTheme.ts';
import {
	FrameGraphSnapshotValidationError,
	decodeFrameGraphSnapshot,
	parseFrameGraphSnapshot,
	stringifyFrameGraphSnapshot,
	type FrameGraphSnapshot,
} from '@zenfg/snapshot';
import {
	createDebugViewModel,
	type FrameGraphDebugSnapshotSource,
	type FrameGraphDebugViewModel,
} from './debugCaptureModel.ts';
import { GraphControls } from './panelGraphControls.ts';
import {
	destroyGraph,
	resolveGraphScene,
	renderGraphLegend,
} from './panelGraphView.ts';
import { graphGroupElementId, selectionKey } from './panelGraphScene.ts';
import { resolveNodeSelection, selectionExists } from './panelSelection.ts';
import type { GraphViewState, Selection, WorkbenchTab } from './panelTypes.ts';
import { ensureFrameGraphInspectorStyles } from './styles.ts';
import { sameSelection, writeClipboardText, type WorkbenchCallbacks } from './panelWorkbenchHelpers.ts';
import { FrameGraphDebugWorkbench } from './panelWorkbenchView.ts';

/** Construction, initial graph visibility, and safety limits for an embedded {@link FrameGraphInspector}. */
export type FrameGraphInspectorOptions = {
	/** Optional CSS theme. Without one, host variables inherit over the Storm defaults. */
	theme?: InspectorTheme;
	/**
	 * Initially show resource declaration nodes in the Graph view. The
	 * Declarations control can change this later; revealing a resource in the
	 * graph also enables it. Does not affect resource tables or Snapshot data.
	 *
	 * @defaultValue `false`
	 */
	showResourceDeclarations?: boolean;
	/**
	 * Product label shown in the workbench command bar. Pass `false` to hide
	 * visible branding while retaining the inspector's accessible name.
	 *
	 * @defaultValue `"ZenFG Inspector"`
	 */
	branding?: string | false;
	/**
	 * Produces a live snapshot when capture is requested. The inspector awaits
	 * promises and displays thrown or rejected errors in its status area.
	 */
	captureSnapshot?: (request: FrameGraphCaptureRequest) => FrameGraphSnapshot | undefined | Promise<FrameGraphSnapshot | undefined>;
	/**
	 * Maximum accepted import size in bytes.
	 *
	 * @defaultValue `67108864` (64 MiB)
	 */
	maxImportBytes?: number;
	/**
	 * Maximum graph nodes plus edges accepted by automatic layout.
	 *
	 * @defaultValue `5000`
	 */
	maxGraphElements?: number;
};

const DEFAULT_MAX_IMPORT_BYTES = 64 * 1024 * 1024;
const DEFAULT_MAX_GRAPH_ELEMENTS = 5_000;
const DEFAULT_BRANDING = 'ZenFG Inspector';

/**
 * Browser lifecycle controller for one embeddable FrameGraph inspector.
 *
 * @remarks The constructor creates, but does not append, {@link dom}. Use
 * {@link mountFrameGraphInspector} for the common append-and-return workflow.
 * Async capture, import, and clipboard failures are reported in the UI. Call
 * {@link destroy} when the controller is no longer needed.
 *
 * @example
 * ```ts
 * const inspector = new FrameGraphInspector({ captureSnapshot: capture });
 * document.body.append(inspector.dom);
 * // Later:
 * inspector.destroy();
 * ```
 */
export class FrameGraphInspector {
	/** Root element owned by this inspector instance. */
	readonly dom = document.createElement('section');
	private readonly themeController = new InspectorThemeController(this.dom);

	private readonly body = document.createElement('div');
	private readonly content = document.createElement('div');
	private readonly dropOverlay = document.createElement('div');
	private readonly graphControls: GraphControls;
	private readonly graphView: GraphViewState;
	private readonly workbench: FrameGraphDebugWorkbench;
	private captureSnapshotCallback: FrameGraphInspectorOptions['captureSnapshot'];
	private protocolSnapshot: FrameGraphSnapshot | undefined;
	private operationRevision = 0;
	private viewModel: FrameGraphDebugViewModel | undefined;
	private selected: Selection | undefined;
	private hovered: Selection | undefined;
	private capturing = false;
	private importing = false;
	private copying = false;
	private copied = false;
	private initialAutoCaptureAttempted = false;
	private statusMessage: string | undefined;
	private statusTone: 'neutral' | 'error' = 'neutral';
	private destroyed = false;
	private copyFeedbackTimeout: number | undefined;
	private readonly maxImportBytes: number;
	private dragDepth = 0;
	private revealRevision = 0;
	private readonly handleDragEnter = (event: DragEvent): void => {
		if (!isFileDrag(event)) return;
		event.preventDefault();
		this.dragDepth += 1;
		this.setDropActive(true);
	};
	private readonly handleDragOver = (event: DragEvent): void => {
		if (!isFileDrag(event)) return;
		event.preventDefault();
		if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
		this.setDropActive(true);
	};
	private readonly handleDragLeave = (event: DragEvent): void => {
		if (!isFileDrag(event) && this.dragDepth === 0) return;
		this.dragDepth = Math.max(0, this.dragDepth - 1);
		if (this.dragDepth === 0) this.setDropActive(false);
	};
	private readonly handleDragEnd = (): void => this.clearDropState();
	private readonly handleDrop = (event: DragEvent): void => {
		const file = event.dataTransfer?.files?.[0];
		if (!file) return;
		event.preventDefault();
		this.clearDropState();
		void this.importSnapshot(file);
	};

	/**
	 * Creates a detached inspector UI.
	 *
	 * @throws If a configured numeric limit is not a non-negative safe integer,
	 * or if browser DOM globals are unavailable.
	 */
	constructor(options: FrameGraphInspectorOptions = {}) {
		ensureFrameGraphInspectorStyles();
		this.captureSnapshotCallback = options.captureSnapshot;
		this.maxImportBytes = normalizeMaxImportBytes(options.maxImportBytes);
		const layoutElementBudget = normalizeLimit(options.maxGraphElements, DEFAULT_MAX_GRAPH_ELEMENTS, 'maxGraphElements');
		const branding = options.branding ?? DEFAULT_BRANDING;
		this.dom.className = 'zenfg-inspector';
		this.dom.id = createInspectorId();
		this.dom.setAttribute('role', 'region');
		this.dom.setAttribute('aria-label', branding === false ? DEFAULT_BRANDING : branding);
		this.body.className = 'zenfg-inspector-body';
		this.content.className = 'zenfg-inspector-content';
		this.dropOverlay.className = 'zenfg-inspector-drop-overlay';
		this.dropOverlay.hidden = true;
		this.dropOverlay.setAttribute('aria-hidden', 'true');
		const dropTitle = document.createElement('strong');
		dropTitle.textContent = 'Drop snapshot to open';
		const dropDetail = document.createElement('span');
		dropDetail.textContent = 'The first file will be imported locally.';
		this.dropOverlay.append(dropTitle, dropDetail);

		const graphLegend = document.createElement('div');
		graphLegend.className = 'zenfg-inspector-graph-legend';
		graphLegend.setAttribute('aria-label', 'Graph legend');
		this.graphControls = new GraphControls({
			onDeclarations: () => this.toggleDeclarations(),
			onGroups: () => this.toggleGroups(),
			onCollapseAll: () => this.collapseAllGroups(),
			onFocusRelations: () => this.toggleFocusRelations(),
			onToggleSelectedGroup: () => { if (this.selected?.kind === 'group') this.toggleGroup(this.selected.pathKey); },
			onZoomBy: (factor) => this.graphView.renderer?.zoomBy?.(factor),
			onResetZoom: () => this.graphView.renderer?.resetZoom?.(),
			onFitSelection: () => { if (this.selected) this.graphView.renderer?.fitSelection?.(this.selected); },
			onFitGraph: () => this.graphView.renderer?.fit(),
		}, this.dom.id);
		this.graphView = {
			host: document.createElement('div'),
			toolbar: this.graphControls.toolbar,
			viewportControls: this.graphControls.viewportControls,
			onViewportChange: (zoom) => this.graphControls.setZoom(zoom),
			legend: graphLegend,
			refreshTheme: () => this.refreshTheme(),
			layoutElementBudget,
			groupsEnabled: true,
			showResourceDeclarations: options.showResourceDeclarations ?? false,
			expandedGroupPaths: new Set(),
			fitOnNextRender: true,
		};
		this.graphView.host.className = 'zenfg-inspector-graph';

		const callbacks: WorkbenchCallbacks = {
			onSelect: (selection) => this.handleSelect(selection),
			onHover: (selection) => this.handleHover(selection),
			onGroupToggle: (pathKey) => this.toggleGroup(pathKey),
			isGroupExpanded: (pathKey) => this.graphView.expandedGroupPaths.has(pathKey),
			onReveal: (selection, tab) => this.handleReveal(selection, tab),
			onNavigate: (tab, filter) => this.workbench.navigate(tab, filter),
		};
		this.workbench = new FrameGraphDebugWorkbench(this.graphView, callbacks, {
			onCapture: () => { void this.captureSnapshot(); },
			onImport: (file) => { void this.importSnapshot(file); },
			onDownload: () => this.downloadSnapshot(),
			onCopyJson: () => { void this.copySnapshotJson(); },
		}, {
			branding,
			idPrefix: this.dom.id,
		});
		this.content.append(this.workbench.root);
		this.body.appendChild(this.content);
		this.dom.append(this.body, this.dropOverlay);
		this.dom.addEventListener('dragenter', this.handleDragEnter);
		this.dom.addEventListener('dragover', this.handleDragOver);
		this.dom.addEventListener('dragleave', this.handleDragLeave);
		this.dom.addEventListener('dragend', this.handleDragEnd);
		this.dom.addEventListener('drop', this.handleDrop);
		this.updateCaptureActions();
		this.updateGraphControls();
		this.showEmptyState();
		if (options.theme) this.setTheme(options.theme);
		queueMicrotask(() => { if (!this.destroyed) { this.refreshTheme(); this.maybeAutoCapture(); } });
	}

    /** Replace API-applied variables on this instance, then synchronize graph styles.
     * Pass null to restore host CSS and defaults. Does not change capture or interaction state.
     */
    setTheme(theme: InspectorTheme | null): void {
        if (this.destroyed) return;
        this.themeController.apply(theme);
        this.refreshTheme();
    }

    /** Re-read final CSS values after external CSS changes and refresh graph and legend.
     * Does not write theme variables, recapture, fit, or rebuild the component.
     */
    refreshTheme(): void {
        if (this.destroyed) return;
        const theme = resolveGraphTheme(this.dom);
        this.graphView.theme = theme;
        this.graphView.renderer?.setTheme?.(theme);
        if (this.viewModel) renderGraphLegend(this.graphView.legend, this.viewModel, theme, this.graphView.showResourceDeclarations);
    }

	/**
	 * Replaces or removes the live-capture provider.
	 *
	 * @remarks Installing a provider may immediately begin an asynchronous
	 * capture when no snapshot is displayed. Replacing or removing a provider
	 * invalidates its pending capture results without cancelling the provider
	 * promise. An already displayed snapshot and any pending import are preserved.
	 */
	setCaptureSnapshotProvider(provider: FrameGraphInspectorOptions['captureSnapshot']): void {
		if (this.destroyed || this.captureSnapshotCallback === provider) return;
		if (this.capturing) {
			this.operationRevision += 1;
			this.capturing = false;
		}
		this.captureSnapshotCallback = provider;
		if (!this.viewModel) this.initialAutoCaptureAttempted = false;
		if (!provider && !this.viewModel && !this.importing) this.showEmptyState();
		this.updateCaptureActions();
		this.maybeAutoCapture();
	}

	/**
	 * Requests and displays a live snapshot from the configured provider.
	 *
	 * @remarks Concurrent requests are coalesced. Provider errors, invalid
	 * snapshots, and an unavailable provider are displayed in the inspector and
	 * do not reject the returned promise.
	 */
	async captureSnapshot(): Promise<void> {
		if (this.destroyed || this.capturing) return;
		if (!this.captureSnapshotCallback) {
			this.reportCaptureIssue('Waiting for a FrameGraph capture source.');
			return;
		}
		const revision = ++this.operationRevision;
		this.importing = false;
		this.capturing = true;
		this.statusMessage = undefined;
		this.statusTone = 'neutral';
		if (!this.viewModel) this.workbench.showEmptyState('capturing', 'Capturing the next rendered frame…');
		this.updateCaptureActions();
		try {
			const request: FrameGraphCaptureRequest = { timing: 'both' };
			const snapshot = await this.captureSnapshotCallback(request);
			if (this.destroyed || revision !== this.operationRevision) return;
			if (!snapshot) {
				this.reportCaptureIssue('No snapshot was produced. Capture again when rendering is active.');
				return;
			}
			const decoded = decodeFrameGraphSnapshot(snapshot);
			if (!decoded.ok) {
				this.reportCaptureIssue(`Failed to capture FrameGraph: ${formatIssues(decoded.issues)}`);
				return;
			}
			this.applySnapshot(decoded.snapshot, { kind: 'live', label: 'Live Capture' });
		}
		catch (error) {
			if (!this.destroyed && revision === this.operationRevision) {
				this.reportCaptureIssue(`Failed to capture FrameGraph: ${error instanceof Error ? error.message : String(error)}`);
			}
		}
		finally {
			if (revision === this.operationRevision) this.capturing = false;
			if (!this.destroyed && revision === this.operationRevision) this.updateCaptureActions();
		}
	}

	/**
	 * Validates and synchronously displays a programmatic Snapshot 1.2 value.
	 * Validation failure preserves the current snapshot and pending capture/import.
	 *
	 * @throws {@link @zenfg/snapshot!index.FrameGraphSnapshotValidationError | FrameGraphSnapshotValidationError} if `snapshot` is invalid.
	 */
	setSnapshot(snapshot: FrameGraphSnapshot): void {
		if (this.destroyed) return;
		const decoded = decodeFrameGraphSnapshot(snapshot);
		if (!decoded.ok) throw new FrameGraphSnapshotValidationError(decoded.issues);
		this.operationRevision += 1;
		this.capturing = false;
		this.importing = false;
		this.applySnapshot(decoded.snapshot, { kind: 'programmatic', label: 'Programmatic' });
	}

	/** Returns the currently displayed canonical snapshot, if any. */
	getSnapshot(): FrameGraphSnapshot | undefined {
		return this.protocolSnapshot;
	}

	/**
	 * Reads, migrates, validates, and displays a snapshot JSON file.
	 *
	 * @remarks Files above `maxImportBytes`, read failures, invalid JSON, and
	 * validation failures are displayed in the UI and do not reject the promise.
	 */
	async importSnapshot(file: File): Promise<void> {
		if (this.destroyed) return;
		const revision = ++this.operationRevision;
		this.capturing = false;
		this.importing = false;
		if (file.size > this.maxImportBytes) {
			this.reportCaptureIssue(`Import exceeds the ${formatImportLimit(this.maxImportBytes)} limit.`);
			return;
		}
		this.importing = true;
		this.statusMessage = undefined;
		this.statusTone = 'neutral';
		this.updateCaptureActions();
		try {
			const text = await file.text();
			if (this.destroyed || revision !== this.operationRevision) return;
			const decoded = parseFrameGraphSnapshot(text);
			if (!decoded.ok) {
				this.reportCaptureIssue(`Failed to import snapshot: ${formatIssues(decoded.issues)}`);
				return;
			}
			this.applySnapshot(decoded.snapshot, {
				kind: 'file',
				label: file.name || 'Imported JSON',
			});
			const sourceFormat = decoded.snapshot.capture.migration?.sourceFormat;
			if (sourceFormat) {
				// The current import format can differ from retained historical provenance.
				const sourceLabel = {
					'legacy-v0': 'Legacy V0',
					'legacy-candidate-v1': 'Legacy Candidate V1',
					'snapshot-v1.1': 'Snapshot 1.1',
				}[decoded.migrated && decoded.source !== 'v1' ? decoded.source : sourceFormat];
				const version = decoded.snapshot.version;
				const targetLabel = `Snapshot ${version.major}.${version.minor}`;
				this.statusMessage = decoded.migrated
					? `Imported ${sourceLabel} and migrated it to ZenFG ${targetLabel}.`
					: `Imported ${targetLabel} with ${sourceLabel} migration provenance.`;
				this.statusTone = 'neutral';
			}
		}
		catch (error) {
			if (!this.destroyed && revision === this.operationRevision) {
				this.reportCaptureIssue(`Failed to import snapshot: ${error instanceof Error ? error.message : String(error)}`);
			}
		}
		finally {
			if (revision === this.operationRevision) this.importing = false;
			if (!this.destroyed && revision === this.operationRevision) this.updateCaptureActions();
		}
	}

	/** Downloads the displayed snapshot as pretty-printed canonical JSON. */
	downloadSnapshot(): void {
		if (this.destroyed || !this.protocolSnapshot) return;
		const json = stringifyFrameGraphSnapshot(this.protocolSnapshot, { pretty: true });
		const url = URL.createObjectURL(new Blob([json], { type: 'application/json;charset=utf-8' }));
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = `frame-graph-${this.protocolSnapshot.capture.frameIndex}.fgsnapshot.json`;
		anchor.click();
		URL.revokeObjectURL(url);
	}

	/**
	 * Copies the displayed snapshot as pretty-printed canonical JSON.
	 *
	 * @remarks Clipboard failures are shown in the UI and do not reject the
	 * returned promise. A legacy `document.execCommand` fallback is used when the
	 * async Clipboard API is unavailable.
	 */
	async copySnapshotJson(): Promise<void> {
		if (this.destroyed || this.copying || !this.protocolSnapshot) return;
		const snapshot = this.protocolSnapshot;
		const revision = this.operationRevision;
		const fallbackFocus = !navigator.clipboard?.writeText ? document.activeElement as HTMLElement | null : null;
		this.copying = true;
		this.copied = false;
		this.statusMessage = undefined;
		this.statusTone = 'neutral';
		this.updateCaptureActions();
		try {
			await writeClipboardText(stringifyFrameGraphSnapshot(snapshot, { pretty: true }));
			if (this.destroyed || revision !== this.operationRevision) return;
			this.copied = true;
			if (this.copyFeedbackTimeout !== undefined) window.clearTimeout(this.copyFeedbackTimeout);
			this.copyFeedbackTimeout = window.setTimeout(() => {
				this.copied = false;
				this.copyFeedbackTimeout = undefined;
				this.updateCaptureActions();
			}, 1200);
		}
		catch (error) {
			if (!this.destroyed && revision === this.operationRevision) {
				this.statusMessage = `Failed to copy snapshot: ${error instanceof Error ? error.message : String(error)}`;
				this.statusTone = 'error';
			}
			console.warn('Failed to copy FrameGraph Snapshot.', error);
		}
		finally {
			this.copying = false;
			if (!this.destroyed) {
				this.updateCaptureActions();
				if (fallbackFocus?.isConnected && (document.activeElement === document.body || document.activeElement === fallbackFocus)) {
					fallbackFocus.focus({ preventScroll: true });
				}
			}
		}
	}

	private applySnapshot(snapshot: FrameGraphSnapshot, source: FrameGraphDebugSnapshotSource): void {
		const viewModel = createDebugViewModel(snapshot, source);
		this.copied = false;
		if (this.copyFeedbackTimeout !== undefined) {
			window.clearTimeout(this.copyFeedbackTimeout);
			this.copyFeedbackTimeout = undefined;
		}
		this.protocolSnapshot = snapshot;
		this.viewModel = viewModel;
		this.graphView.fitOnNextRender = true;
		this.graphView.captureRevision = (this.graphView.captureRevision ?? 0) + 1;
		this.graphView.revealOnNextRender = undefined;
		this.graphView.renderer?.cancelReveal?.();
		this.hovered = undefined;
		this.initialAutoCaptureAttempted = true;
		this.statusMessage = undefined;
		this.statusTone = 'neutral';
		if (this.selected?.kind === 'node' || this.selected?.kind === 'culled') this.selected = resolveNodeSelection(viewModel, this.selected.id);
		else if (this.selected && !selectionExists(viewModel, this.selected)) this.selected = undefined;
		this.updateGraphControls();
		this.workbench.setSnapshot(viewModel, this.selected);
		this.updateCaptureActions();
	}

	/**
	 * Idempotently cancels pending UI results, releases graph resources, and
	 * removes the inspector workbench. Do not reuse the controller afterward.
	 */
	destroy(): void {
		if (this.destroyed) return;
		this.destroyed = true;
		this.operationRevision += 1;
		if (this.copyFeedbackTimeout !== undefined) window.clearTimeout(this.copyFeedbackTimeout);
		this.clearDropState();
		this.dom.removeEventListener('dragenter', this.handleDragEnter);
		this.dom.removeEventListener('dragover', this.handleDragOver);
		this.dom.removeEventListener('dragleave', this.handleDragLeave);
		this.dom.removeEventListener('dragend', this.handleDragEnd);
		this.dom.removeEventListener('drop', this.handleDrop);
		this.themeController.destroy();
		this.graphControls.destroy();
		destroyGraph(this.graphView);
		this.workbench.destroy();
		this.dom.remove();
	}

	private updateCaptureActions(): void {
		this.workbench.setSnapshotActionState({
			providerAvailable: Boolean(this.captureSnapshotCallback),
			hasCapture: Boolean(this.viewModel),
			capturing: this.capturing,
			importing: this.importing,
			copying: this.copying,
			copied: this.copied,
			message: this.viewModel ? this.statusMessage : undefined,
			messageTone: this.statusTone,
		});
	}

	private maybeAutoCapture(): void {
		if (
			this.destroyed
			|| this.viewModel
			|| this.initialAutoCaptureAttempted
			|| this.capturing
			|| this.importing
			|| !this.captureSnapshotCallback
		) return;
		this.initialAutoCaptureAttempted = true;
		void this.captureSnapshot();
	}
	private handleSelect(selection: Selection): void {
		if (!this.viewModel || !selectionExists(this.viewModel, selection)) return;
		this.selected = selection;
		if (selection.kind !== 'node' && selection.kind !== 'group') this.graphView.focusRelations = false;
		this.updateGraphControls();
		this.workbench.setSelection(selection);
	}

	private handleReveal(selection: Selection, tab: WorkbenchTab): void {
		const snapshot = this.viewModel;
		if (!snapshot || !selectionExists(snapshot, selection)) return;
		if (tab === 'graph') {
			const expanded = new Set(this.graphView.expandedGroupPaths);
			const groupsEnabled = this.graphView.groupsEnabled;
			const showResourceDeclarations = this.graphView.showResourceDeclarations;
			if (selection.kind === 'resource') this.graphView.showResourceDeclarations = true;
			if (selection.kind === 'group') this.graphView.groupsEnabled = true;
			let groupId: string | undefined;
			if (selection.kind === 'node') groupId = snapshot.nodeById.get(selection.id)?.debugGroupId;
			else if (selection.kind === 'resource') groupId = snapshot.resourceById.get(selection.id)?.debugGroupId;
			else if (selection.kind === 'group') groupId = snapshot.groupByPathKey.get(selection.pathKey)?.parentId;
			if (this.graphView.groupsEnabled && groupId) {
				for (const id of snapshot.groupById.get(groupId)?.ancestorIds ?? []) {
					const group = snapshot.groupById.get(id);
					if (group) this.graphView.expandedGroupPaths.add(group.pathKey);
				}
			}
			const scene = resolveGraphScene(this.graphView, snapshot);
			const availableIds = new Set([...scene.nodes, ...scene.edges].map((entry) => entry.id));
			if (!(scene.interaction.primaryElementIdsBySelection.get(selectionKey(selection))?.some((id) => availableIds.has(id)))) {
				this.graphView.groupsEnabled = groupsEnabled;
				this.graphView.showResourceDeclarations = showResourceDeclarations;
				this.graphView.expandedGroupPaths.clear();
				for (const path of expanded) this.graphView.expandedGroupPaths.add(path);
				this.workbench.showNavigationIssue(selection.kind === 'culled' ? 'Culled passes are not part of Frame Flow.' : 'This object has no representation in Frame Flow.', selection);
				return;
			}
			this.graphView.revealOnNextRender = { selection, revision: ++this.revealRevision };
			this.updateGraphControls();
		}
		this.handleSelect(selection);
		this.workbench.reveal(selection, tab);
	}

	private handleHover(selection: Selection | undefined): void {
		if (!this.viewModel || sameSelection(this.hovered, selection)) return;
		this.hovered = selection;
		this.workbench.setHovered(selection);
	}

	private updateGraphControls(): void {
		const scene = this.viewModel ? resolveGraphScene(this.graphView, this.viewModel) : undefined;
		const viewportAvailable = Boolean(scene && scene.nodes.length + scene.edges.length <= (this.graphView.layoutElementBudget ?? Number.MAX_SAFE_INTEGER));
		const ids = this.selected && scene?.interaction.primaryElementIdsBySelection.get(selectionKey(this.selected));
		const selected = this.selected;
		const representationAvailable = viewportAvailable && Boolean(selected?.kind === 'group'
			? scene?.nodes.some((node) => node.kind === 'group' && node.groupPathKey === selected.pathKey)
			: ids?.some((id) => scene?.nodes.some((node) => node.id === id) || scene?.edges.some((edge) => edge.id === id)));
		if (!representationAvailable) this.graphView.focusRelations = false;
		this.graphControls.update({
			snapshot: this.viewModel,
			selected: this.selected,
			showResourceDeclarations: this.graphView.showResourceDeclarations ?? false,
			groupsEnabled: this.graphView.groupsEnabled,
			expandedGroupPaths: this.graphView.expandedGroupPaths,
			focusRelations: this.graphView.focusRelations ?? false,
			representationAvailable,
			viewportAvailable,
		});
	}

	private toggleFocusRelations(): void {
		if (this.selected?.kind !== 'node' && this.selected?.kind !== 'group') return;
		this.graphView.focusRelations = !this.graphView.focusRelations;
		this.updateGraphControls();
		this.workbench.refreshGraphStructure();
	}

	private toggleDeclarations(): void {
		this.handleHover(undefined);
		this.graphView.showResourceDeclarations = !(this.graphView.showResourceDeclarations ?? false);
		this.graphView.renderer?.cancelReveal?.();
		this.graphView.revealOnNextRender = undefined;
		this.graphView.anchorElementIdOnNextRender = undefined;
		this.graphView.fitOnNextRender = true;
		this.updateGraphControls();
		this.workbench.refreshGraphStructure();
	}

	private toggleGroups(): void {
		if (!this.viewModel?.debugGroups.length) return;
		this.handleHover(undefined);
		this.graphView.groupsEnabled = !this.graphView.groupsEnabled;
		this.graphView.fitOnNextRender = true;
		this.updateGraphControls();
		this.workbench.refreshGraphStructure();
	}

	private toggleGroup(pathKey: string): void {
		this.handleHover(undefined);
		if (this.graphView.expandedGroupPaths.has(pathKey)) this.graphView.expandedGroupPaths.delete(pathKey);
		else this.graphView.expandedGroupPaths.add(pathKey);
		this.graphView.anchorElementIdOnNextRender = graphGroupElementId(pathKey);
		this.updateGraphControls();
		this.workbench.refreshGraphStructure();
	}

	private collapseAllGroups(): void {
		if (this.graphView.expandedGroupPaths.size === 0) return;
		this.handleHover(undefined);
		this.graphView.expandedGroupPaths.clear();
		this.graphView.fitOnNextRender = true;
		this.updateGraphControls();
		this.workbench.refreshGraphStructure();
	}

	private showEmptyState(): void {
		const providerAvailable = Boolean(this.captureSnapshotCallback);
		this.workbench.showEmptyState(
			'empty',
			providerAvailable
				? 'No capture yet. Use Capture to request the next rendered frame.'
				: 'Drop a ZenFG Snapshot here or choose Import.',
			providerAvailable ? undefined : 'Files are processed locally in your browser.',
		);
	}

	private setDropActive(active: boolean): void {
		if (this.destroyed) return;
		this.dropOverlay.hidden = !active;
		this.dom.classList.toggle('drop-active', active);
	}

	private clearDropState(): void {
		this.dragDepth = 0;
		this.dropOverlay.hidden = true;
		this.dom.classList.remove('drop-active');
	}

	private reportCaptureIssue(message: string): void {
		this.statusMessage = message;
		this.statusTone = 'error';
		if (!this.viewModel) this.workbench.showEmptyState('error', message);
		this.updateCaptureActions();
	}
}

/**
 * Creates an inspector, appends its root element to `host`, and returns its
 * lifecycle controller.
 *
 * @throws If inspector construction fails; see {@link FrameGraphInspector}.
 */
export function mountFrameGraphInspector(host: HTMLElement, options?: FrameGraphInspectorOptions): FrameGraphInspector {
	const inspector = new FrameGraphInspector(options);
	host.appendChild(inspector.dom);
	inspector.refreshTheme();
	return inspector;
}

function normalizeMaxImportBytes(value: number | undefined): number {
	return normalizeLimit(value, DEFAULT_MAX_IMPORT_BYTES, 'maxImportBytes');
}

function normalizeLimit(value: number | undefined, fallback: number, name: string): number {
	if (value === undefined) return fallback;
	if (!Number.isSafeInteger(value) || value < 0) {
		throw new Error(`${name} must be a non-negative safe integer.`);
	}
	return value;
}

function formatImportLimit(bytes: number): string {
	return bytes >= 1024 * 1024 && bytes % (1024 * 1024) === 0
		? `${bytes / (1024 * 1024)} MiB`
		: `${bytes} bytes`;
}

function formatIssues(issues: readonly { readonly path: string; readonly message: string }[]): string {
	return issues.map((issue) => `${issue.path || '/'}: ${issue.message}`).join('\n');
}

function isFileDrag(event: DragEvent): boolean {
	return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

let nextInspectorId = 0;
function createInspectorId(): string {
	nextInspectorId += 1;
	return `zenfg-inspector-${nextInspectorId}`;
}
